import React, { useState } from 'react';
import { AnnotationStatsComponent } from './annotation-stats';
import { AnnotationBrowser, type AnnotationRecord } from './annotation-browser';
import { ParticipantBrowser, type ParticipantRecord } from './participant-browser';
import { ExportDialog } from './export-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart3,
  Search,
  Users,
  Download,
  RefreshCw,
  TrendingUp,
  Database,
  Clock,
  Music,
  RotateCcw
} from 'lucide-react';
import { adminService, type AnnotationFilters } from '@/client/services/admin';

import { toast } from 'sonner';

interface AdminAnnotationDashboardProps {
  className?: string;
}

export function AdminAnnotationDashboard({ className }: AdminAnnotationDashboardProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [quickStats, setQuickStats] = useState<any>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Refresh will be handled by the AnnotationStatsComponent
      toast.success('Dashboard data refreshed');
    } catch (error) {
      toast.error('Failed to refresh dashboard data');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Callback to receive stats from the AnnotationStatsComponent
  const handleStatsUpdate = (stats: any) => {
    setQuickStats(stats);
    setStatsError(null); // Clear any previous errors
  };

  // Callback to receive errors from the AnnotationStatsComponent
  const handleStatsError = (error: string) => {
    setStatsError(error);
  };

  // Format time helper
  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const handleViewAnnotationDetails = (annotation: AnnotationRecord) => {
    console.log('Viewing annotation details:', annotation.id);
    // Additional logic for viewing annotation details
  };

  const handleViewParticipantDetails = (participant: ParticipantRecord) => {
    console.log('Viewing participant details:', participant.id);
    // Additional logic for viewing participant details
  };

  const handleExportData = async (filters: AnnotationFilters) => {
    try {
      await adminService.downloadAnnotationExport(filters, 'csv');
      toast.success('Data export started - download will begin shortly');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportParticipants = async (filters: any) => {
    try {
      await adminService.downloadParticipantExport('csv', { includeAnnotationStats: true });
      toast.success('Participant export started - download will begin shortly');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export participant data');
    }
  };

  const [isResettingSessions, setIsResettingSessions] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleResetSessions = async () => {
    setIsResettingSessions(true);
    try {
      const result = await adminService.resetSessions();
      toast.success(result.message);
      setShowResetConfirm(false);
      handleRefresh();
    } catch (error) {
      console.error('Reset sessions failed:', error);
      toast.error('Failed to reset sessions');
    } finally {
      setIsResettingSessions(false);
    }
  };

  return (
    <div className={className}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Annotation Dashboard</h1>
            <p className="text-muted-foreground">
              Monitor and analyze music annotation research data
            </p>
          </div>
          <div className="flex gap-2">
            {showResetConfirm ? (
              <div className="flex items-center gap-2 p-2 border border-destructive/50 rounded-md bg-destructive/5">
                <span className="text-sm text-destructive">Cancel all active sessions?</span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleResetSessions}
                  disabled={isResettingSessions}
                >
                  {isResettingSessions ? 'Resetting...' : 'Confirm'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResetConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowResetConfirm(true)}
                className="gap-2 text-destructive hover:text-destructive"
              >
                <RotateCcw className="h-4 w-4" />
                Reset Sessions
              </Button>
            )}
            <ExportDialog
              exportType="both"
              trigger={
                <Button variant="outline" className="gap-2">
                  <Database className="h-4 w-4" />
                  Export Data
                </Button>
              }
            />
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh All
            </Button>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {quickStats ? quickStats.activeSessions : (statsError ? 'Error' : 'Loading...')}
              </div>
              <p className="text-xs text-muted-foreground">
                Currently in progress
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Today's Annotations</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {quickStats ? quickStats.recentActivity.annotationsToday : (statsError ? 'Error' : 'Loading...')}
              </div>
              <p className="text-xs text-muted-foreground">
                {quickStats?.recentActivity?.annotationsThisWeek ? `+${quickStats.recentActivity.annotationsThisWeek} this week` : 'See details below'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">New Participants</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {quickStats ? quickStats.recentActivity.newParticipantsToday : (statsError ? 'Error' : 'Loading...')}
              </div>
              <p className="text-xs text-muted-foreground">
                Today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Annotations</CardTitle>
              <Music className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {quickStats ? quickStats.totalAnnotations.toLocaleString() : (statsError ? 'Error' : 'Loading...')}
              </div>
              <p className="text-xs text-muted-foreground">
                All time
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Error Display */}
        {statsError && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <TrendingUp className="h-4 w-4" />
                <span className="font-medium">Authentication Required</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Please log in as an admin user to view detailed statistics. Showing sample data below.
              </p>
            </CardContent>
          </Card>
        )}



        {/* Main Dashboard Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="annotations" className="gap-2">
              <Search className="h-4 w-4" />
              Annotations
            </TabsTrigger>
            <TabsTrigger value="participants" className="gap-2">
              <Users className="h-4 w-4" />
              Participants
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <AnnotationStatsComponent 
              onRefresh={handleRefresh}
              onStatsUpdate={handleStatsUpdate}
              onError={handleStatsError}
              className="w-full"
            />
          </TabsContent>

          <TabsContent value="annotations" className="space-y-4">
            <AnnotationBrowser
              onViewDetails={handleViewAnnotationDetails}
              onExport={handleExportData}
              className="w-full"
            />
          </TabsContent>

          <TabsContent value="participants" className="space-y-4">
            <ParticipantBrowser
              onViewDetails={handleViewParticipantDetails}
              onExport={handleExportParticipants}
              className="w-full"
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}